import { Licenses } from "../Licenses";
import { PackAttribution, PackAuthor, PackLicense, PackSource } from "../Types";
import { AddButton } from "./AddButton";
import { SelectButton } from "./SelectButton";

type AttributionEditorProps = {
  attribution: PackAttribution;
  onAddAuthor: () => void;
  onAddLicense: () => void;
  onAddSource: () => void;
  onEditAuthor: (author: PackAuthor, index: number) => void;
  onEditLicense: (license: PackLicense) => void;
  onEditSource: (source: PackSource, index: number) => void;
  onRemoveAuthor: (index: number) => void;
  onRemoveLicense: () => void;
  onRemoveSource: (index: number) => void;
};

const licenseShortcodes = Object.keys(Licenses);

export const AttributionEditor = (props: AttributionEditorProps) => {
  const {
    attribution,
    onAddAuthor,
    onAddLicense,
    onAddSource,
    onEditAuthor,
    onEditLicense,
    onEditSource,
    onRemoveAuthor,
    onRemoveLicense,
    onRemoveSource,
  } = props;

  const disableLicenseEditing = attribution.license &&
    attribution.license.shortname !== "other";

  return (
    <div>
      {attribution.license != null
        ? (
          <>
            <div className="section">💡 license</div>

            <div className="license">
              <div className="tiny-header">
                LICENSE
              </div>
              {/* dropdown */}
              <select
                value={attribution.license.shortname}
                onChange={(e) => {
                  onEditLicense({
                    ...attribution.license,
                    shortname: e.target.value,
                  });
                }}
              >
                {[...licenseShortcodes, "other"].map((license) => (
                  <option key={license} value={license}>
                    {license}
                  </option>
                ))}
              </select>
              <div className="tiny-header">
                LICENSE NAME
              </div>
              <input
                disabled={disableLicenseEditing}
                type="text"
                className={disableLicenseEditing ? "disabled" : ""}
                value={attribution.license.name}
                onChange={disableLicenseEditing
                  ? ((e) => {
                    onEditLicense({
                      ...attribution.license!,
                      name: e.target.value,
                    });
                  })
                  : undefined}
              />
              <div className="tiny-header">
                LICENSE URL
              </div>
              <input
                disabled={disableLicenseEditing}
                type="text"
                className={disableLicenseEditing ? "disabled" : ""}
                value={attribution.license.url}
                onChange={disableLicenseEditing
                  ? ((e) => {
                    onEditLicense({
                      ...attribution.license!,
                      url: e.target.value,
                    });
                  })
                  : undefined}
              />
              <div className="tiny-header">
                LICENSE FULL TEXT
              </div>
              <textarea
                disabled={disableLicenseEditing}
                value={attribution.license.fullText}
                className={disableLicenseEditing ? "disabled" : ""}
                onChange={disableLicenseEditing
                  ? ((e) => {
                    onEditLicense({
                      ...attribution.license!,
                      fullText: e.target.value,
                    });
                  })
                  : undefined}
              >
              </textarea>

              <button className="btn" onClick={onRemoveLicense}>
                remove license
              </button>
            </div>
          </>
        )
        : <AddButton onClick={onAddLicense} title="add license" />}
      {(attribution.authors || []).map((author, index) => (
        <div className="author" key={index}>
          <div className="section">💡 author</div>
          <div className="name">
            <div className="tiny-header">
              AUTHOR NAME
            </div>
            <input
              type="text"
              value={author.name}
              onChange={(e) => {
                const newName = e.target.value;
                onEditAuthor({ ...author, name: newName }, index);
              }}
            />
          </div>
          <div className="url">
            <div className="tiny-header">
              AUTHOR URL
            </div>
            <input
              type="text"
              value={author.url}
              onChange={(e) => {
                const newUrl = e.target.value;
                onEditAuthor({ ...author, url: newUrl }, index);
              }}
            />
          </div>
          <div className="image">
            <div className="tiny-header">
              AUTHOR IMAGE URL
            </div>
            <input
              type="text"
              value={author.image}
              onChange={(e) => {
                const newImageUrl = e.target.value;
                onEditAuthor({ ...author, image: newImageUrl }, index);
              }}
            />
          </div>

          <div className="image">
            <div className="tiny-header">
              AUTHOR TYPE
            </div>
            <div className="row">
              <SelectButton
                selected={author.type === "primary"}
                onClick={() => {
                  onEditAuthor({ ...author, type: "primary" }, index);
                }}
                text="primary"
              />
              <SelectButton
                selected={author.type === "secondary"}
                onClick={() => {
                  onEditAuthor({ ...author, type: "secondary" }, index);
                }}
                text="secondary"
              />
            </div>
          </div>
          <div className="remove">
            <button
              onClick={() =>
                onRemoveAuthor(index)}
            >
              🗑
            </button>
          </div>
        </div>
      ))}
      {(attribution.sources || []).map((source, index) => (
        <div className="source" key={index}>
          <div className="section">💡 source</div>
          <div className="name">
            <div className="tiny-header">
              SOURCE NAME
            </div>
            <input
              type="text"
              value={source.name}
              onChange={(e) => {
                const newName = e.target.value;
                onEditSource({ ...source, name: newName }, index);
              }}
            />
          </div>
          <div className="url">
            <div className="tiny-header">
              SOURCE URL
            </div>
            <input
              type="text"
              value={source.url}
              onChange={(e) => {
                const newUrl = e.target.value;
                onEditSource({ ...source, url: newUrl }, index);
              }}
            />
          </div>
          <div className="license-name">
            <div className="tiny-header">
              SOURCE LICENSE NAME
            </div>
            <input
              type="text"
              value={source.license?.name || ""}
              onChange={(e) => {
                const newLicenseName = e.target.value;
                if (source.license) {
                  onEditSource(
                    {
                      ...source,
                      license: { ...source.license, name: newLicenseName },
                    },
                    index,
                  );
                } else {
                  onEditSource({
                    ...source,
                    license: { shortname: "other", name: newLicenseName },
                  }, index);
                }
              }}
            />
          </div>
          <div className="license-url">
            <div className="tiny-header">
              SOURCE LICENSE URL
            </div>
            <input
              type="text"
              value={source.license?.url || ""}
              onChange={(e) => {
                const newLicenseUrl = e.target.value;
                if (source.license) {
                  onEditSource(
                    {
                      ...source,
                      license: { ...source.license, url: newLicenseUrl },
                    },
                    index,
                  );
                } else {
                  onEditSource(
                    {
                      ...source,
                      license: { shortname: "other", url: newLicenseUrl },
                    },
                    index,
                  );
                }
              }}
            />
          </div>
          <div className="description">
            <div className="tiny-header">
              SOURCE DESCRIPTION
            </div>
            <textarea
              value={source.description}
              onChange={(e) => {
                const newDescription = e.target.value;
                onEditSource({ ...source, description: newDescription }, index);
              }}
            />
          </div>
          <div className="remove">
            <button
              onClick={() =>
                onRemoveSource(index)}
            >
              🗑
            </button>
          </div>
        </div>
      ))}
      <AddButton onClick={onAddAuthor} title="add author" />
      <AddButton onClick={onAddSource} title="add source" />
    </div>
  );
};
